import React, { useState, useEffect, useMemo } from "react";
import { useProfile } from "../../contexts/ProfileContext";
import { FaUserCircle } from "react-icons/fa";
import SimpleBar from "simplebar-react";
import { useChat } from "../../contexts/ChatContext";
import ChatLoader from "../../components/ChatLoader/ChatLoader";
import ChatListSkeleton from "../../components/ChatListSkeleton/ChatListSkeleton";
const ChatLeftSidebar = ({ openUserChat, recentChatUserList, recentCustomerChatUserList, setAllChats, isChatListLoading }) => {
    const { profileData } = useProfile();

    const [searchChat, setSearchChat] = useState("");
    const [filteredChatList, setFilteredChatList] = useState([]);
    const [customerChatList, setCustomerChatList] = useState([]);
    const [activeChatId, setActiveChatId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingChatId, setLoadingChatId] = useState(null);
    const [failedImages, setFailedImages] = useState(new Set());

    const allFlattenedMessages = useMemo(() => {
        const messages = [];

        const processChatList = (list, isCustomerSupport = false) => {
            list.forEach((chat) => {
                const chatInfo = {
                    ...chat,
                    isCustomerSupport,
                    isProperty: !chat.first_name,
                };

                (chat.messages || []).forEach((msg) => {
                    messages.push({ ...msg, chatInfo, isLastMessage: false });
                });

                if (chat.last_message) {
                    messages.push({ ...chat.last_message, chatInfo, isLastMessage: true });
                }
            });
        };

        processChatList(recentChatUserList);
        processChatList(recentCustomerChatUserList, true);

        return messages;
    }, [recentChatUserList, recentCustomerChatUserList]);

    useEffect(() => {
        if (!searchChat.trim()) {
            setFilteredChatList(recentChatUserList);
            setCustomerChatList(recentCustomerChatUserList);
            return;
        }

        const term = searchChat.toLowerCase();

        // First, check for direct conversation name matches
        const directMatches = [];

        // Check regular chat conversations
        recentChatUserList.forEach((chat) => {
            const receiverName = chat.receiver_user_name?.toLowerCase() || "";
            const firstName = chat.first_name?.toLowerCase() || "";
            const lastName = chat.last_name?.toLowerCase() || "";
            const ownerName = chat.owner_name?.toLowerCase() || "";
            const propertyAddress = chat.property_address?.toLowerCase() || "";

            if (
                receiverName.includes(term) ||
                firstName.includes(term) ||
                lastName.includes(term) ||
                ownerName.includes(term) ||
                propertyAddress.includes(term)
            ) {
                directMatches.push({
                    ...chat,
                    isSearchResult: true,
                    isCustomerSupport: false,
                });
            }
        });

        // Check customer service conversations
        recentCustomerChatUserList.forEach((chat) => {
            const receiverName = chat.receiver_user_name?.toLowerCase() || "";
            const firstName = chat.first_name?.toLowerCase() || "";
            const lastName = chat.last_name?.toLowerCase() || "";
            const ownerName = chat.owner_name?.toLowerCase() || "";
            const propertyAddress = chat.property_address?.toLowerCase() || "";

            if (
                receiverName.includes(term) ||
                firstName.includes(term) ||
                lastName.includes(term) ||
                ownerName.includes(term) ||
                propertyAddress.includes(term) ||
                "customer support".includes(term)
            ) {
                directMatches.push({
                    ...chat,
                    isSearchResult: true,
                    isCustomerSupport: true,
                });
            }
        });

        // Then, search through message content
        const matches = allFlattenedMessages.filter((msg) => {
            const text = msg.message?.toLowerCase() || "";
            const sender = msg.sender_name?.toLowerCase() || "";
            return text.includes(term) || sender.includes(term) || (msg.media && "attachment".includes(term));
        });

        const conversationMap = new Map();
        matches.forEach((msg) => {
            const info = msg.chatInfo;
            const key = info.isCustomerSupport
                ? `support-${info.property_id || info.id}`
                : info.isProperty
                  ? `property-${info.property_id}`
                  : `user-${info.user_id}`;

            if (!conversationMap.has(key)) {
                conversationMap.set(key, {
                    ...info,
                    matchingMessages: [],
                    bestMatch: msg,
                });
            }

            const conv = conversationMap.get(key);
            conv.matchingMessages.push(msg);

            if (msg.message?.toLowerCase().includes(term)) {
                conv.bestMatch = msg;
            }
        });

        const messageSearchResults = Array.from(conversationMap.values()).map((conv) => ({
            ...conv,
            isSearchResult: true,
            highlightMessage: conv.bestMatch.message,
            last_message: conv.bestMatch.isLastMessage ? conv.bestMatch : { ...conv.bestMatch, isHighlighted: true },
            first_name: conv.first_name || conv.owner_name?.split(" ")[0] || "",
            last_name: conv.last_name || conv.owner_name?.split(" ")[1] || "",
            profile_pic: conv.profile_pic || null,
        }));

        // Combine direct matches with message search results, avoiding duplicates
        const allMatches = [...directMatches];
        messageSearchResults.forEach((result) => {
            const isDuplicate = directMatches.some((direct) => {
                const directKey = direct.isCustomerSupport
                    ? `support-${direct.property_id || direct.id}`
                    : direct.property_address
                      ? `property-${direct.property_id}`
                      : `user-${direct.user_id}`;
                const resultKey = result.isCustomerSupport
                    ? `support-${result.property_id || result.id}`
                    : result.isProperty
                      ? `property-${result.property_id}`
                      : `user-${result.user_id}`;
                return directKey === resultKey;
            });

            if (!isDuplicate) {
                allMatches.push(result);
            }
        });

        setFilteredChatList(allMatches.filter((c) => !c.isCustomerSupport));
        setCustomerChatList(allMatches.filter((c) => c.isCustomerSupport));
    }, [searchChat, allFlattenedMessages, recentChatUserList, recentCustomerChatUserList]);

    const handleSearchChange = (e) => setSearchChat(e.target.value);

    const openChat = async (e, chat, type) => {
        // Prevent multiple selections when already loading
        if (isLoading) return;

        // Set loading state
        setIsLoading(true);
        setLoadingChatId(chat?.id || chat?.property_id || "customerSupport");

        try {
            // Add a small delay to show the loader (simulating network request)
            await new Promise((resolve) => setTimeout(resolve, 800));

            const navParams = { messageId: chat.id || null, scrollToMessage: !!chat.id };
            if (chat.id) setActiveChatId(chat.id);

            if (type === "property") openUserChat(e, { ...chat, ...navParams }, "property");
            else if (chat === "customerSupport") openUserChat(e, "customerSupport", null, navParams);
            else openUserChat(e, { ...chat, ...navParams });

            document.querySelector(".chats-sidebar-container")?.classList.add("hidden-chat");
            document.querySelector(".chats-container")?.classList.remove("hidden-chat");
            document.querySelector(".chats-container")?.classList.add("visible-chat");
            document.querySelector(".chats-sidebar-container")?.classList.remove("visible-chat");
        } catch (error) {
            console.error("Error opening chat:", error);
        } finally {
            // Clear loading state
            setIsLoading(false);
            setLoadingChatId(null);
        }
    };

    const highlight = (text = "", term = "") => {
        if (!term) return text;
        const parts = text.split(new RegExp(`(${term})`, "gi"));
        return parts.map((part, i) => (part.toLowerCase() === term.toLowerCase() ? <mark key={i}>{part}</mark> : part));
    };

    const getChatProfilePicture = (chat) => {
        if (chat.user_id == profileData.id) {
            const url = "http://image.onmls.com" + chat.receiver_user_profile_image;
            return url;
        } else {
            const user_url = "http://image.onmls.com" + chat.user_profile_image;
            return user_url;
        }
    };

    // Helper function to get chat initials for display when no profile image
    const getChatInitials = (chat) => {
        const receiverName = chat.receiver_user_name || chat.owner_name || '';
        const firstName = chat.first_name || '';
        const lastName = chat.last_name || '';

        if (firstName && lastName) {
            return `${firstName[0]?.toUpperCase() || ''}${lastName[0]?.toUpperCase() || ''}`;
        }

        if (receiverName) {
            const nameParts = receiverName.split(' ');
            if (nameParts.length >= 2) {
                return `${nameParts[0][0]?.toUpperCase() || ''}${nameParts[1][0]?.toUpperCase() || ''}`;
            }
            return receiverName[0]?.toUpperCase() || '?';
        }

        return '?';
    };

    // Check if profile image URL is valid
    const hasValidProfileImage = (chat) => {
        const chatId = chat?.id || chat?.property_id;
        if (failedImages.has(chatId)) return false;
        const imageUrl = getChatProfilePicture(chat);
        return imageUrl && !imageUrl.endsWith('null') && !imageUrl.endsWith('undefined');
    };

    // Handle image load error
    const handleImageError = (chatId) => {
        setFailedImages(prev => new Set([...prev, chatId]));
    };
    const renderChatItem = (chat) => {
        const name = chat.receiver_user_name ? chat.receiver_user_name : "Customer Support";
        const lastMsg = chat.last_message;
        const chatItemId = chat?.id || chat?.property_id || "customerSupport";
        const isCurrentlyLoading = loadingChatId === chatItemId;

        return (
            <li
                key={chat.id}
                onClick={(e) => {
                    // Prevent clicks when any chat is loading
                    if (isLoading) return;

                    if (chat.conversation_type === "customer_to_customer") return openChat(e, chat);
                    if (!chat.property_address) return openChat(e, "customerSupport");
                    if (chat.property_address) return openChat(e, chat, "property");
                    openChat(e, chat);
                }}
                className={`tw-relative tw-p-2 tw-transition-all tw-duration-200 ${
                    isLoading ? "tw-cursor-not-allowed tw-opacity-60" : "tw-cursor-pointer hover:tw-bg-gray-100"
                } ${activeChatId === chat._id ? "tw-bg-indigo-50 tw-border-l-4 tw-border-indigo-500" : "tw-border-b tw-border-gray-200"} ${
                    isCurrentlyLoading ? "tw-bg-blue-50 tw-border-l-4 tw-border-blue-400" : ""
                }`}
            >
                <div className="tw-flex tw-items-start tw-gap-3">
                    {chat.receiver_user_profile_image && hasValidProfileImage(chat) ? (
                        <img
                            src={getChatProfilePicture(chat)}
                            className="tw-rounded-full tw-w-10 tw-h-10 tw-object-cover tw-bg-primary"
                            alt="avatar"
                            onError={() => handleImageError(chat?.id || chat?.property_id)}
                        />
                    ) : (
                        <div className="tw-rounded-full tw-w-10 tw-h-10 tw-bg-primary tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-bold tw-text-white">
                            {getChatInitials(chat)}
                        </div>
                    )}
                    <div className="tw-flex-1 tw-min-w-0">
                        <p className={`tw-truncate tw-font-medium ${chat?.unread_count ? "tw-text-black" : "tw-text-gray-600"}`}>
                            {highlight(name, searchChat)}
                        </p>
                        {chat.property_address && <p className="tw-truncate tw-text-xs tw-text-gray-500">{chat.property_address}</p>}
                        <p className="tw-truncate tw-text-xs tw-text-gray-500">{highlight(lastMsg, searchChat)}</p>
                    </div>

                    {/* Individual chat loading indicator */}
                    {isCurrentlyLoading && (
                        <div className="tw-flex tw-items-center tw-justify-center">
                            <div className="tw-w-5 tw-h-5 tw-border-2 tw-border-blue-200 tw-border-t-blue-600 tw-rounded-full tw-animate-spin"></div>
                        </div>
                    )}
                </div>
            </li>
        );
    };
    // Show skeleton loader while initial chat list is loading
    if (isChatListLoading) {
        return (
            <div className="tw-bg-white tw-border-r tw-h-[calc(100vh)] md:tw-h-[calc(100vh-55px)] md:tw-mt-[55px] tw-overflow-auto">
                <ChatListSkeleton />
            </div>
        );
    }

    return (
        <React.Fragment>
            {/* Chat Loading Overlay */}
            <ChatLoader isLoading={isLoading} selectedChatId={loadingChatId} />

            <div
                className={`chat-leftsidebar me-lg-1 tw-bg-white tw-border-r tw-h-[calc(100vh)] md:tw-h-[calc(100vh-55px)] md:tw-mt-[55px] tw-overflow-auto ${
                    isLoading ? "tw-pointer-events-none tw-select-none" : ""
                }`}
            >
                <div className="tw-h-full tw-bg-white tw-flex tw-flex-col">
                    <div className="tw-px-5 md:tw-h-[55px] tw-flex tw-mt-[18px] tw-items-center tw-justify-between tw-border-b tw-bg-background_color">
                        <h3 className="tw-text-lg tw-font-bold tw-pb-[8px]">
                            Messages <span className="tw-text-xs">{recentChatUserList?.length || 0}</span>
                        </h3>
                    </div>
                    <div className="tw-px-3 tw-py-2 tw-bg-white">
                        <div className="tw-flex tw-items-center tw-bg-gray-100 tw-rounded-md tw-px-3 tw-py-1">
                            <i className="ri-search-line tw-text-gray-500" />
                            <input
                                type="text"
                                value={searchChat}
                                onChange={handleSearchChange}
                                className="tw-ml-2 tw-bg-transparent tw-outline-none tw-w-full tw-text-sm"
                                placeholder="Search messages and users"
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                    <SimpleBar className="tw-flex-1">
                        <ul className="tw-list-none tw-m-0 tw-p-0">{filteredChatList.map((chat) => renderChatItem(chat))}</ul>
                    </SimpleBar>
                </div>
            </div>
        </React.Fragment>
    );
};

export default ChatLeftSidebar;
